/*
   ****** The below React component is developed by AECS Private Limited. ******
   Please don't remove, replace, or edit this header information
   Component: <Alarms.js>
   Description: This React component handles the display of all chambers data status. It will show all the pot devices status.
   Dependencies: This component uses Material-UI components, moment for date formatting, and custom components like Header, Footer, and Sidebar.
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/Devices.css';
import { ContentContainer, MainContent, CenteredCard, StyledTableHeader, RefreshIcon, HeaderTitle } from '../Styles/Devices';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import { Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Clear as ClearIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useLogoTitle } from '../classes/LogoTitleContext';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import moment from 'moment';
import {
  TextField,
  InputAdornment,
} from '@mui/material';
import Header from '../Parts/Header';
import Footer from '../Parts/Footer';
import Sidebar from '../Parts/Sidebar';
import getAuthHeaders from '../Auth/Authentication';
import { API_ENDPOINTS } from '../Auth/apiConfig';
import refreshIcon from '../Images/refresh-icon.svg';
import axios from 'axios';
import {
  EdgesensorHighOutlined as OnDeviceTrainingIcon,
  SensorsOutlined as GppGoodIcon,
  MyLocationOutlined as MyLocationOutlinedIcon,
  ElectricalServicesOutlined as AlarmOnIcon,
  CircleNotificationsOutlined as AlarmActiveIcon
} from '@mui/icons-material';
import floatONvalveOFF from '../Images/DevicesIcons/floatONvalveOFF.png';
import floatONValveON from '../Images/DevicesIcons/floatONValveON.png';
import floatOFFvalveON from '../Images/DevicesIcons/floatOFFvalveON.png';
import floatOFFvalveOFF from '../Images/DevicesIcons/floatOFFvalveOFF.png';
import CircularProgress from '@mui/material/CircularProgress';

// Function to determine the image source based on status
const getImageSrc = (valveStatus, floatStatus) => {
  if (valveStatus === 'OK' && floatStatus === 'Alarm') return floatONvalveOFF;
  if (valveStatus === 'Alarm' && floatStatus === 'Alarm') return floatONValveON;
  if (valveStatus === 'Alarm' && floatStatus === 'OK') return floatOFFvalveON;
  return floatOFFvalveOFF;
};

const Devices = ({ onLogout, loggedInUser }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const { logoUrl } = useLogoTitle();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const isRestrictedClientForLoRa = (user) => user && (user.clientId == 509 || user.clientId == 510);

  useEffect(() => {
    const fetchData = async () => {
      const params = {
        IntTimeDiff: loggedInUser.TimeDiff,
        clientId: loggedInUser.clientId
      };
      try {
        const authHeaders = getAuthHeaders();
        const response = await axios.get(`${API_ENDPOINTS.sensordevices}`, {
          params: params,
          headers: authHeaders
        });
        //console.log('Response data:', response.data);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        //toast.error('Failed to fetch data');
      }
    };

    fetchData();
  }, [loggedInUser]);

  const handleSort = (columnName) => {
    const direction = columnName === sortColumn && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(columnName);
    setSortDirection(direction);

    // Sort data based on column and direction
    let sortedData = [...data];
    switch (columnName) {
      case 'DeviceName':
        sortedData.sort((a, b) => {
          if (direction === 'asc') {
            return a.pot.localeCompare(b.pot);
          } else {
            return b.pot.localeCompare(a.pot);
          }
        });
        break;
      case 'Alarm':
        sortedData.sort((a, b) => {
          if (direction === 'asc') {
            return a.globalStatus.localeCompare(b.globalStatus);
          } else {
            return b.globalStatus.localeCompare(a.globalStatus);
          }
        });
        break;
      default:
        break;
    }
    setData(sortedData);
  };

  const sortedData = () => {
    return data;
  };

  const filteredData = () => {
    if (!searchTerm.trim()) {
      return data;
    }
    return data.filter((rowData) =>
      rowData.pot.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleOpenModal = async (potName, item) => {
    try {
      setLoading(true); // Start loading

      // Define date range
      const currentDate = moment().format('YYYY-MM-DD');
      const oneDayAgo = moment().subtract(1, 'days').format('YYYY-MM-DD');
      const authHeaders = getAuthHeaders();

      // Fetch data from API
      const response = await axios.post(
        `${API_ENDPOINTS.DevicePagePotClick}`,
        {
          row: potName, // Use potName directly
          selectedDateFrom: oneDayAgo,
          selectedDateTo: currentDate,
          projectID: loggedInUser.Projectid,
          TimeDiff: loggedInUser.TimeDiff,
          clientId: loggedInUser.clientId,
        },
        {
          headers: authHeaders,
        }
      );

      console.log('Response Data of POTCLICK:', response.data);

      if (response.data && response.data.length > 0) {
        // Assume row 0 is for valve and row 1 is for float
        const valveRow = response.data[0] || {};
        const floatRow = response.data[1] || {};

        // Update the item with the relevant data
        const updatedItem = {
          ...item,
          pot: valveRow.pot || 'NA',
          valveStatus: valveRow.valveStatus || 'NA',
          valveCycles: valveRow.ValveTotalRows || '0',
          valveDate: valveRow.AdjustedValveStatusTime || 'NA',
          floatStatus: floatRow.floatStatus || 'NA',
          floatCycles: floatRow.FloatTotalRows || '0',
          floatDate: floatRow.AdjustedFloatStatusTime || 'NA',

          // ✅ New fields from backend
          totalValveCycles: valveRow.totalValveCycles || '0',
          totalValveDuration: valveRow.totalValveDurationFormatted || '00:00:00',
        };

        setSelectedItem(updatedItem); // Update selected item
      } else {
        // Handle the case when no data is returned
        setSelectedItem(null);
      }
    } catch (error) {
      console.error('Error fetching Pot click data:', error);
      // Optionally show an error message to users
      // toast.error('Error fetching data');
    } finally {
      setLoading(false); // Stop loading
      setModalOpen(true); // Open modal
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  return (
    <div style={{ backgroundColor: '#EBEFF3' }}>
      <Sidebar loggedInUser={loggedInUser} />
      <Header
        logoUrl={logoUrl}
        loggedInUser={loggedInUser}
        onLogout={onLogout}
      />

      <ContentContainer>
        <MainContent>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} lg={20}>
              <CenteredCard>
                <CardContent style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Fixed Header */}
                  <div style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    padding: '10px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <HeaderTitle>Pot Status</HeaderTitle>
                      </Box>

                      {/* Search Field */}
                      <TextField
                        label="Search by pot name"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClearSearch}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          flex: 1,
                          maxWidth: '220px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#ccc',
                            },
                            '&:hover fieldset': {
                              borderColor: '#888',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                              borderWidth: '1px',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#555',
                            '&.Mui-focused': {
                              color: '#1976d2',
                            },
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '14px',
                            color: '#333',
                          },
                          '& .MuiIconButton-root': {
                            color: '#1976d2',
                          },
                          // Responsive styles
                          '@media (max-width: 600px)': {
                            flex: '1 0 100%', // Allow search field to take full width
                            marginTop: '8px', // Add some space above the search field
                          },
                        }}
                      />

                      {/* Sort Icon */}
                      <IconButton
                        onClick={() => handleSort('Alarm')}
                        sx={{
                          padding: '8px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '50%',
                          '&:hover': {
                            backgroundColor: '#e0e0e0',
                          },
                          marginLeft: '5px',
                          // Responsive styles
                          '@media (max-width: 600px)': {
                            marginLeft: '0', // Remove left margin for mobile
                            marginTop: '8px', // Add top margin for spacing in column layout
                          },
                        }}
                      >
                        {sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                      </IconButton>
                    </div>

                    {/* Refresh Icon */}
                    <RefreshIcon
                      title="Refresh"
                      src={refreshIcon}
                      label="Click to refresh"
                      alt="Refresh"
                      onClick={() => window.location.reload()}
                    />
                  </div>

                  {/* Scrollable Content */}
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingTop: '10px',
                  }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', justifyContent: 'center' }}>
                      {filteredData().map((rowData, index) => (
                        <div
                          key={index}
                          style={{
                            backgroundColor: '#f0f0f0',
                            borderRadius: '12px',
                            border: '1px solid #eaeaea',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            width: '220px',
                            padding: '14px',
                            position: 'relative',
                            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                          onClick={() => handleOpenModal(rowData.pot, rowData)}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                          {/* Pot Name */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                            <OnDeviceTrainingIcon sx={{ marginRight: '6px', color: '#1976d2', fontSize: '20px' }} />
                            <h3 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: 'bold' }}>{rowData.pot || 'NA'}</h3>
                          </div>

                          {/* Address Section */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                            <MyLocationOutlinedIcon sx={{ marginRight: '6px', color: '#388e3c', fontSize: '18px' }} />
                            <span style={{ color: '#333', fontSize: '16px' }}>{rowData.addrStreet || '---'}</span>
                          </div>

                          {/* Master Module Section */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                            <AlarmOnIcon sx={{ marginRight: '6px', color: '#ff9800', fontSize: '18px' }} />
                            <span style={{ color: '#333', fontSize: '16px' }}>Master module: {rowData.masterModule || 'NA'}</span>
                          </div>

                          {/* Alarm Status 
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <AlarmActiveIcon sx={{ marginRight: '6px', color: '#f44336', fontSize: '18px' }} />
                            <span style={{ color: '#333', fontSize: '16px' }}>Alarm status: {rowData.globalStatus || 'NA'}</span>
                          </div>
                          */}
                          {/* Status Banner */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '16px',
                              backgroundColor: rowData.globalStatus === 'OK' ? '#4caf50' : '#f44336',
                              color: '#fff',
                              padding: '3px 6px',
                              borderRadius: '6px',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                          >
                            {rowData.globalStatus === 'OK' ? 'OK' : 'ALARM'}
                          </div>
                        </div>
                      ))}
                      {loading && (
                        <Box
                          sx={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress
                            size={40}
                            thickness={2}
                            sx={{
                              color: 'transparent',
                              '& .MuiCircularProgress-circle': {
                                stroke: 'url(#gradient)',
                              },
                            }}
                          />
                          <svg width="0" height="0">
                            <defs>
                              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: '#42a5f5', stopOpacity: 1 }} />
                                <stop offset="100%" style={{ stopColor: '#1976d2', stopOpacity: 1 }} />
                              </linearGradient>
                            </defs>
                          </svg>
                        </Box>
                      )}
                    </div>
                  </div>
                  <Dialog
                    open={modalOpen}
                    onClose={handleCloseModal}
                    maxWidth="sm"
                    fullWidth
                    sx={{
                      '& .MuiDialog-paper': {
                        width: '550px',
                        maxWidth: '100%',
                        position: 'relative', // Ensure the dialog paper has relative positioning for the absolute button
                      },
                    }}
                  >
                    <DialogTitle
                      sx={{
                        textAlign: 'center',
                        borderBottom: '3px solid #1976d2',
                        paddingBottom: '12px',
                        marginBottom: '16px',
                        bgcolor: '#e3f2fd',
                        color: '#0d47a1',
                        fontWeight: '600',
                        fontSize: '1.25rem',
                        letterSpacing: '0.5px',
                        borderRadius: '4px',
                        padding: '8px 0',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '1.5rem',
                          color: '#0d47a1',
                          textAlign: 'center',
                          fontWeight: '200',
                          letterSpacing: '0.75px',
                          fontFamily: "'Roboto', sans-serif",
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        Pot Overview
                      </Typography>
                      <IconButton
                        onClick={handleCloseModal}
                        sx={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          color: '#0d47a1',
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </DialogTitle>

                    <DialogContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '10px',
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            padding: '24px',
                            marginTop: '65px',
                            //backgroundColor: '#f9f9f9', 
                            border: '2px solid #d3d3d3',
                            borderRadius: '8px', // Rounded corners
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Soft shadow for depth
                          }}
                        >
                          {selectedItem ? (
                            <>
                              <Typography
                                sx={{
                                  marginBottom: '8px',
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  color: '#333', // Dark text for readability
                                }}
                              >
                                <strong>Pot name:</strong> {selectedItem.pot || 'NA'}
                              </Typography>
                              <Typography
                                sx={{
                                  marginBottom: '8px',
                                  fontSize: '16px',
                                  color: '#333',
                                }}
                              >
                                <strong>Valve status:</strong>{' '}
                                <span
                                  style={{
                                    color:
                                      selectedItem.valveStatus === 'Alarm'
                                        ? 'red'
                                        : selectedItem.valveStatus === 'OK'
                                          ? 'green'
                                          : 'inherit',
                                  }}
                                >
                                  {selectedItem.valveStatus || 'NA'}
                                </span>
                              </Typography>
                              <Typography
                                sx={{
                                  marginBottom: '8px',
                                  fontSize: '16px',
                                  color: '#333',
                                }}
                              >
                                <strong>Float status:</strong>{' '}
                                <span
                                  style={{
                                    color:
                                      selectedItem.floatStatus === 'Alarm'
                                        ? 'red'
                                        : selectedItem.floatStatus === 'OK'
                                          ? 'green'
                                          : 'inherit',
                                  }}
                                >
                                  {selectedItem.floatStatus || 'NA'}
                                </span>
                              </Typography>
                              <Typography
                                sx={{
                                  marginBottom: '8px',
                                  fontSize: '16px',
                                  color: '#333',
                                }}
                              >
                                <strong>
                                  {(loggedInUser && (loggedInUser.clientId == 509 || loggedInUser.clientId == 510))
                                    ? 'Total Cycles:'
                                    : 'Valve cycles:'}
                                </strong>{' '}
                                {(loggedInUser && (loggedInUser.clientId == 509 || loggedInUser.clientId == 510))
                                  ? (selectedItem?.totalValveCycles || '0')
                                  : (selectedItem?.valveCycles || '0')}
                              </Typography>
                              {(loggedInUser && (loggedInUser.clientId == 509 || loggedInUser.clientId == 510)) && (
                                <Typography
                                  sx={{
                                    marginBottom: '8px',
                                    fontSize: '16px',
                                    color: '#333',
                                  }}
                                >
                                  <strong>Total Duration:</strong>
                                  <div>{selectedItem?.totalValveDuration || '00:00:00'}</div>
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: '16px',
                                color: '#666',
                              }}
                            >
                              Loading...
                            </Typography>
                          )}
                          {!isRestrictedClientForLoRa(loggedInUser) && (
                            <Typography
                              component="span"
                              sx={{
                                color: 'dodgerblue',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                              }}
                            >
                              Last 1 day
                            </Typography>
                          )}
                        </Box>

                        {selectedItem && (
                          <Box
                            component="img"
                            src={getImageSrc(selectedItem.valveStatus, selectedItem.floatStatus)}
                            alt="Valve and Float Alarm"
                            sx={{
                              width: '250px',
                              height: 'auto',
                              marginLeft: '16px',
                              borderRadius: '10px',
                              border: '2px solid #d3d3d3',  // Light grey border
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                        )}
                      </Box>
                    </DialogContent>
                    {selectedItem && (
                      <Typography
                        sx={{
                          marginBottom: '5px',
                          textAlign: 'center',
                        }}
                      >
                        {!isRestrictedClientForLoRa(loggedInUser) && (
                          <Link
                            to={`/PotOverview/${selectedItem.pot || 'NA'}`}
                            style={{
                              textDecoration: 'underline',
                              color: '#1a73e8',
                              fontSize: '16px',
                              transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#0c47a1')}
                            onMouseLeave={(e) => (e.target.style.color = '#1a73e8')}
                          >
                            Click here for pot detailed analytics
                            <Typography
                              component="span"
                              sx={{
                                color: '#1a73e8',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                ml: 0.5,
                              }}
                            >
                              (Last 7 days)
                            </Typography>
                          </Link>
                        )}
                      </Typography>
                    )}
                  </Dialog>
                </CardContent>
              </CenteredCard>
            </Grid>
          </Grid>
        </MainContent>
      </ContentContainer>
      <Footer />
      <ToastContainer />
    </div >
  );
};

export default Devices;